import { BookOpenCheck, Search } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button, Card } from '../components/hybrid'
import { HomeInspector } from '../components/home/home-inspector'
import { HomeQuickActions } from '../components/home/home-quick-actions'
import {
  buildHomeBriefing,
  buildHomeWorkQueue,
  findReadinessCheck,
} from '../components/home/home-signal-helpers'
import {
  HomeWorkQueueTable,
  type HomeWorkQueueItem,
} from '../components/home/home-work-queue-table'
import { HomeWorkspaceKnowledge } from '../components/home/home-workspace-knowledge'
import { HomeReadinessCard } from '../components/readiness/home-readiness-card'
import { useActivity, type ActivityEvent } from '../hooks/use-activity'
import { useAuth } from '../hooks/use-auth'
import { useCreditBalance } from '../hooks/use-credits'
import { buildCreateDocumentBody, useCreateDocument, useDocuments } from '../hooks/use-documents'
import { useHostedSites } from '../hooks/use-hosted-sites'
import { useMcpHealth } from '../hooks/use-mcp-health'
import { useReadiness } from '../hooks/use-readiness'
import { useUploads } from '../hooks/use-uploads'
import { getCreditErrorGuidance } from '../lib/credit-error-guidance'
import { getDocumentKindInfo } from '../lib/document-kind'
import { getWebFeatureProfile } from '../lib/feature-flags'
import { roleHasPermission } from '../lib/role-permissions'

function formatNumber(value: number | undefined) {
  return new Intl.NumberFormat('en-US').format(value ?? 0)
}

function formatActivityTime(createdAt: number) {
  const deltaMs = Date.now() - createdAt
  const minutes = Math.max(0, Math.floor(deltaMs / 60_000))
  if (minutes < 1) return 'now'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

function activityText(event: ActivityEvent) {
  const actor = event.actorName ?? event.actorEmail ?? 'System'
  const resource = event.resourceType ? ` ${event.resourceType}` : ''
  return `${actor}: ${event.label}${resource}`
}

export function HomePage() {
  const storageEnabled = getWebFeatureProfile().storageSpace
  const navigate = useNavigate()
  const { user, role } = useAuth()
  const canCreateDocument = roleHasPermission(role, 'doc:create')
  const canCreateAgentKey = roleHasPermission(role, 'key:create')
  const canReviewAnalytics = roleHasPermission(role, 'tenant:manage')
  const canReadStorage = storageEnabled && roleHasPermission(role, 'storage:read')
  const canUploadStorage = storageEnabled && roleHasPermission(role, 'storage:upload')
  const createDocument = useCreateDocument()
  const documents = useDocuments({ limit: 24, sort: 'updatedAt', order: 'desc' })
  const uploads = useUploads(canReadStorage)
  const hostedSites = useHostedSites()
  const creditBalance = useCreditBalance()
  const readiness = useReadiness()
  const activity = useActivity(6)
  const mcpHealth = useMcpHealth()

  const documentCount = readiness.data?.workspace.documentCount ?? documents.data?.total ?? 0
  const recentDocuments = documents.data?.data ?? []
  const verifiedDocuments = recentDocuments.filter(
    (doc) => getDocumentKindInfo(doc).kind === 'verified',
  )
  const syncedSourceCount = readiness.data?.sources?.sourceCount ?? 0
  const knowledgeItemCount = readiness.data?.memory?.total ?? 0
  const uploadStatusUnavailable = canReadStorage && uploads.isError
  const uploadFiles = uploadStatusUnavailable ? [] : (uploads.data?.files ?? [])
  const readinessFileCounts = readiness.data?.files
  const uploadFileCount = canReadStorage ? (readinessFileCounts?.total ?? uploadFiles.length) : 0
  const hostedSiteCount =
    readiness.data?.workspace.hostedSiteCount ??
    hostedSites.data?.total ??
    hostedSites.data?.sites.length ??
    0
  const activeUploads = uploadFiles.filter((file) => file.extractionStatus !== 'completed')
  const failedUploads = uploadFiles.filter((file) => file.extractionStatus === 'failed')
  const processingUploads = uploadFiles.filter(
    (file) => file.extractionStatus === 'processing' || file.extractionStatus === 'pending',
  )
  const failedUploadCount = canReadStorage
    ? (readinessFileCounts?.failed ?? failedUploads.length)
    : 0
  const processingUploadCount = canReadStorage
    ? (readinessFileCounts?.processing ?? processingUploads.length)
    : 0
  const workspaceName = user?.name ? `${user.name}'s workspace` : 'Workspace'
  const starterOrPurchasedCredits = creditBalance.data?.purchasedCredits ?? 0
  const creditSubtext = creditBalance.isLoading
    ? 'checking balance'
    : starterOrPurchasedCredits > 0
      ? `${formatNumber(starterOrPurchasedCredits)} starter or purchased available`
      : `${formatNumber(creditBalance.data?.freeCredits)} resettable free available`

  const briefing = buildHomeBriefing({
    summary: readiness.data,
    isLoading: readiness.isLoading,
    failedUploadCount,
    processingUploadCount,
    uploadStatusUnavailable,
  })
  const workQueue: HomeWorkQueueItem[] = buildHomeWorkQueue({
    activeUploads,
    readiness: readiness.data,
    owner: user?.name ?? 'You',
    uploadStatusUnavailable,
    storageEnabled: canReadStorage,
  })
  const searchReadinessCheck = findReadinessCheck(readiness.data, 'raw-source-guard')
  const activityItems = (activity.data?.events ?? []).map((event) => ({
    time: formatActivityTime(event.createdAt),
    text: activityText(event),
  }))

  const handleCreateDocument = async () => {
    if (!canCreateDocument) return
    try {
      const payload = buildCreateDocumentBody({
        title: 'Untitled',
        source: 'the AgentWiki Home quick action',
      })
      const doc = await createDocument.mutateAsync(payload)
      navigate(`/doc/${doc.slug}/edit`)
    } catch (err) {
      const guidance = getCreditErrorGuidance(err)
      if (guidance) {
        toast.error(guidance.title, {
          description: guidance.description,
          action: { label: guidance.actionLabel, onClick: () => navigate(guidance.actionHref) },
        })
        return
      }
      toast.error('Document creation failed', {
        description: err instanceof Error ? err.message : undefined,
      })
    }
  }

  return (
    <div className="aw-home">
      <div className="aw-home-main">
        <section className="aw-page">
          <header className="aw-page-head">
            <h1 className="aw-home-hero">Agent Knowledge Ops</h1>
            <div className="aw-home-sub">
              {workspaceName} · {formatNumber(syncedSourceCount)} synced sources ·{' '}
              {formatNumber(documentCount)} documents · {formatNumber(knowledgeItemCount)} knowledge
              items
            </div>
          </header>

          <div className="aw-page-body">
            <section className="aw-brief-grid">
              <div className="aw-home-primary-stack">
                <div className="aw-brief">
                  <span className="aw-label">Today briefing</span>
                  <h2>{briefing.heading}</h2>
                  <p>{briefing.body}</p>
                  <div className="aw-brief-actions">
                    {verifiedDocuments.length > 0 ? (
                      <Button to="/doc?ownership=source-backed" variant="primary">
                        <BookOpenCheck className="h-4 w-4" />
                        Read source-backed docs
                      </Button>
                    ) : (
                      <Button to="/search" variant="primary">
                        <Search className="h-4 w-4" />
                        Search
                      </Button>
                    )}
                    {canReviewAnalytics && (
                      <Button to="/settings/search-analytics">
                        <Search className="h-4 w-4" />
                        Review search analytics
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <HomeReadinessCard
                summary={readiness.data}
                isLoading={readiness.isLoading}
                variant="compact"
                storageEnabled={canReadStorage}
              />
            </section>

            <HomeWorkspaceKnowledge
              documents={recentDocuments}
              isLoading={documents.isLoading}
              summary={readiness.data}
              workQueue={workQueue}
              uploadStatusUnavailable={uploadStatusUnavailable}
            />

            <div className="aw-group-label">Workspace operations</div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
              <Card>
                <div className="aw-stat-lab">Documents</div>
                <div className="aw-stat-num">{formatNumber(documentCount)}</div>
                <div className="aw-stat-d" style={{ color: 'var(--ok)' }}>
                  {documents.isLoading ? 'loading count' : 'ready for agents'}
                </div>
              </Card>
              {canReadStorage && (
                <Card>
                  <div className="aw-stat-lab">Uploaded files</div>
                  <div className="aw-stat-num">{formatNumber(uploadFileCount)}</div>
                  <div
                    className="aw-stat-d"
                    style={{ color: uploadStatusUnavailable ? 'var(--warn)' : 'var(--proc)' }}
                  >
                    {uploadStatusUnavailable ? (
                      'upload status unavailable'
                    ) : (
                      <>
                        {processingUploadCount} indexing ·{' '}
                        <span style={{ color: 'var(--dgr)' }}>{failedUploadCount} failed</span>
                      </>
                    )}
                  </div>
                </Card>
              )}
              <Card>
                <div className="aw-stat-lab">Credits</div>
                <div className="aw-stat-num">{formatNumber(creditBalance.data?.totalCredits)}</div>
                <div className="aw-stat-d" style={{ color: 'var(--ink-3)' }}>
                  {creditSubtext}
                </div>
              </Card>
              <Link to="/sites" className="aw-card aw-stat-link">
                <div className="aw-stat-lab">Artifact publishing</div>
                <div className="aw-stat-num">{formatNumber(hostedSiteCount)}</div>
                <div
                  className="aw-stat-d"
                  style={{ color: hostedSites.isError ? 'var(--warn)' : 'var(--ink-3)' }}
                >
                  {hostedSites.isError
                    ? 'artifact status unavailable · Manage artifacts'
                    : 'Manage artifacts'}
                </div>
              </Link>
            </div>

            <div className="aw-group-label">Quick actions</div>
            <HomeQuickActions
              onCreateDocument={handleCreateDocument}
              onUploadFile={() => navigate('/storage')}
              onCreateAgentKey={() => navigate('/agents')}
              onConnectMcp={() => navigate('/agents')}
              createPending={createDocument.isPending}
              showUpload={canUploadStorage}
              showCreateDocument={canCreateDocument}
              showCreateAgentKey={canCreateAgentKey}
            />

            <div className="aw-group-label">Open work queue</div>
            <HomeWorkQueueTable items={workQueue} />
          </div>
        </section>
      </div>

      <HomeInspector
        processingCount={processingUploadCount}
        failedCount={failedUploadCount}
        uploadStatusUnavailable={uploadStatusUnavailable}
        storageEnabled={canReadStorage}
        activityItems={activityItems}
        activityUnavailable={activity.isError}
        searchReadinessCheck={searchReadinessCheck}
        mcpHealth={{
          isLoading: mcpHealth.isLoading,
          isError: mcpHealth.isError,
          status: mcpHealth.data?.status,
        }}
      />
    </div>
  )
}
